import { Lexer, type Token, type Tokens } from 'marked';
import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import styles from './MarkdownContent.module.css';
import { isSafeMarkdownUrl } from '../../lib/markdown-links';
export { isSafeMarkdownUrl } from '../../lib/markdown-links';

export type MarkdownDisplayMode = 'preview' | 'source';

type MarkdownContentProps = {
  source: string;
  inline?: boolean;
  className?: string;
};

/** Plain, single-line text for dense CRM list and board previews. */
export function markdownToPlainText(source: string): string {
  const tokens = Lexer.lex(source, { gfm: true, breaks: false });
  return tokens
    .map(tokenToPlainText)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function MarkdownContent({ source, inline = false, className }: MarkdownContentProps) {
  const tokens = useMemo(
    () => inline
      ? Lexer.lexInline(source, { gfm: true, breaks: false })
      : Lexer.lex(source, { gfm: true, breaks: false }),
    [inline, source],
  );

  const classes = [styles.markdown, inline ? styles.inline : null, className]
    .filter(Boolean)
    .join(' ');

  return inline
    ? <span className={classes}>{renderInlineTokens(tokens)}</span>
    : <div className={classes}>{renderBlockTokens(tokens)}</div>;
}

export function MarkdownModeToggle({
  mode,
  onChange,
  label = 'Markdown view',
}: {
  mode: MarkdownDisplayMode;
  onChange: (mode: MarkdownDisplayMode) => void;
  label?: string;
}) {
  return (
    <div className={styles.toggle} role="group" aria-label={label}>
      <button type="button" data-active={mode === 'preview'} aria-pressed={mode === 'preview'} onClick={() => onChange('preview')}>
        Preview
      </button>
      <button type="button" data-active={mode === 'source'} aria-pressed={mode === 'source'} onClick={() => onChange('source')}>
        Markdown
      </button>
    </div>
  );
}

/** Both modes share the same Markdown draft; preview never rewrites its source. */
export function MarkdownEditor({ id, label, value, onChange, required = false }: {
  id: string; label: string; value: string; onChange: (value: string) => void; required?: boolean;
}) {
  const [mode, setMode] = useState<MarkdownDisplayMode>('source');
  const textarea = useRef<HTMLTextAreaElement>(null);
  const selection = useRef({ start: 0, end: 0, scrollTop: 0 });
  const returningToSource = useRef(false);

  const changeMode = (next: MarkdownDisplayMode) => {
    if (next === mode) return;
    if (textarea.current) {
      const { selectionStart: start, selectionEnd: end, scrollTop } = textarea.current;
      selection.current = { start, end, scrollTop };
    }
    returningToSource.current = next === 'source';
    setMode(next);
  };
  useEffect(() => {
    if (!returningToSource.current || !textarea.current) return;
    returningToSource.current = false;
    textarea.current.focus();
    textarea.current.setSelectionRange(selection.current.start, selection.current.end);
    textarea.current.scrollTop = selection.current.scrollTop;
  }, [mode]);

  return <div className={styles.editor}>
    <MarkdownModeToggle mode={mode} onChange={changeMode} label={`${label} editor mode`} />
    {mode === 'source' ? <textarea ref={textarea} id={id} aria-label={label} required={required}
      className={styles.editorInput} rows={14} placeholder="Write in Markdown…"
      value={value} onChange={event => onChange(event.target.value)} />
      : <div className={styles.editorPreview} role="region" aria-label={`${label} preview`} tabIndex={0}>
        {value.trim() ? <MarkdownContent source={value} /> : <p className={styles.empty}>Nothing to preview yet.</p>}
      </div>}
  </div>;
}

export function MarkdownViewer({
  source,
  className,
  emptyFallback = '—',
}: {
  source: string;
  className?: string;
  emptyFallback?: string;
}) {
  const [mode, setMode] = useState<MarkdownDisplayMode>('preview');
  if (!source.trim()) return <span className={styles.empty}>{emptyFallback}</span>;

  return (
    <div className={[styles.viewer, className].filter(Boolean).join(' ')}>
      {mode === 'preview'
        ? <MarkdownContent source={source} />
        : <MarkdownSource source={source} />}
      <MarkdownModeToggle mode={mode} onChange={setMode} />
    </div>
  );
}

export function MarkdownSource({ source }: { source: string }) {
  return <pre className={styles.source}><code>{source}</code></pre>;
}

function renderBlockTokens(tokens: readonly Token[]): ReactNode[] {
  return tokens.map((token, index) => renderBlockToken(token, index));
}

function renderBlockToken(token: Token, key: number | string): ReactNode {
  switch (token.type) {
    case 'space':
    case 'def':
      return null;
    case 'paragraph':
      return <p key={key}>{renderInlineTokens((token as Tokens.Paragraph).tokens)}</p>;
    case 'text':
      return token.tokens
        ? <Fragment key={key}>{renderInlineTokens(token.tokens)}</Fragment>
        : <Fragment key={key}>{token.text}</Fragment>;
    case 'heading': {
      const heading = token as Tokens.Heading;
      // The page already owns h1; Markdown headings begin one level below it.
      const Tag = `h${Math.min(heading.depth + 1, 6)}` as keyof JSX.IntrinsicElements;
      return <Tag key={key}>{renderInlineTokens(heading.tokens)}</Tag>;
    }
    case 'blockquote':
      return <blockquote key={key}>{renderBlockTokens((token as Tokens.Blockquote).tokens)}</blockquote>;
    case 'code':
      return renderCode(token as Tokens.Code, key);
    case 'hr':
      return <hr key={key} />;
    case 'list': {
      const list = token as Tokens.List;
      const Tag = list.ordered ? 'ol' : 'ul';
      const start = list.ordered && typeof list.start === 'number' && list.start !== 1
        ? list.start
        : undefined;
      return (
        <Tag key={key} start={start}>
          {list.items.map((item, itemIndex) => renderListItem(item, itemIndex))}
        </Tag>
      );
    }
    case 'table':
      return renderTable(token as Tokens.Table, key);
    case 'html':
      // Model-authored HTML is never interpreted. React escapes this string.
      return <p key={key}>{token.raw}</p>;
    default:
      return <Fragment key={key}>{token.raw}</Fragment>;
  }
}

function renderCode(token: Tokens.Code, key: number | string): ReactNode {
  return (
    <pre key={key}>
      <code data-language={safeLanguage(token.lang)}>{token.text}</code>
    </pre>
  );
}

function renderListItem(item: Tokens.ListItem, key: number): ReactNode {
  return (
    <li key={key}>
      {item.task ? <input type="checkbox" checked={Boolean(item.checked)} readOnly disabled /> : null}
      {renderBlockTokens(item.tokens)}
    </li>
  );
}

function renderTable(token: Tokens.Table, key: number | string): ReactNode {
  const alignment = (align: Tokens.TableCell['align']): CSSProperties | undefined =>
    align ? { textAlign: align } : undefined;
  return (
    <div className={styles.tableScroll} key={key}>
      <table>
        <thead>
          <tr>
            {token.header.map((cell, index) => (
              <th key={index} style={alignment(cell.align)}>{renderInlineTokens(cell.tokens)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {token.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} style={alignment(cell.align)}>{renderInlineTokens(cell.tokens)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderInlineTokens(tokens: readonly Token[]): ReactNode[] {
  return tokens.map((token, index) => renderInlineToken(token, index));
}

function renderInlineToken(token: Token, key: number): ReactNode {
  switch (token.type) {
    case 'text':
      return token.tokens
        ? <Fragment key={key}>{renderInlineTokens(token.tokens)}</Fragment>
        : <Fragment key={key}>{token.text}</Fragment>;
    case 'escape':
      return <Fragment key={key}>{token.text}</Fragment>;
    case 'strong':
      return <strong key={key}>{renderInlineTokens((token as Tokens.Strong).tokens)}</strong>;
    case 'em':
      return <em key={key}>{renderInlineTokens((token as Tokens.Em).tokens)}</em>;
    case 'del':
      return <del key={key}>{renderInlineTokens((token as Tokens.Del).tokens)}</del>;
    case 'codespan':
      return <code key={key}>{token.text}</code>;
    case 'br':
      return <br key={key} />;
    case 'link':
      return renderLink(token as Tokens.Link, key);
    case 'image':
      // Remote model-authored images are intentionally not fetched.
      return <span key={key} className={styles.imageAlt}>{(token as Tokens.Image).text || 'Image'}</span>;
    case 'html':
      return <Fragment key={key}>{token.raw}</Fragment>;
    default:
      return <Fragment key={key}>{token.raw}</Fragment>;
  }
}

function renderLink(token: Tokens.Link, key: number): ReactNode {
  return isSafeMarkdownUrl(token.href)
        ? (
            <a key={key} href={token.href} target="_blank" rel="noopener noreferrer" title={token.title ?? undefined}>
              {renderInlineTokens(token.tokens)}
            </a>
          )
        : <Fragment key={key}>{renderInlineTokens(token.tokens)}</Fragment>;
}

function safeLanguage(value: string | undefined): string | undefined {
  const language = value?.trim().split(/\s+/)[0];
  return language && /^[a-z0-9_+-]+$/i.test(language) ? language : undefined;
}

function tokenToPlainText(token: Token): string {
  if (token.type === 'table') {
    const table = token as Tokens.Table;
    return [...table.header, ...table.rows.flat()]
      .map((cell) => cell.tokens.map(tokenToPlainText).join(''))
      .join(' ');
  }
  if (token.type === 'list') {
    return (token as Tokens.List).items
      .flatMap((item) => item.tokens)
      .map(tokenToPlainText)
      .join(' ');
  }
  if (token.type === 'html') return token.raw.replace(/<[^>]*>/g, ' ');
  if ('tokens' in token && Array.isArray(token.tokens)) {
    return token.tokens.map(tokenToPlainText).join('');
  }
  if ('text' in token && typeof token.text === 'string') return token.text;
  return '';
}
